/**
 * Framing hints while lining up a shot (SPEC_CAPTURE_UX_R2 §5) — ADVISORY, never a gate: shown as a small line under
 * the instruction card before the shutter, cleared when the condition clears; the shutter is never disabled by them and
 * the server-side quality gate stays the authority. A device that cannot sample (no canvas) shows none — never a fake one.
 *
 *   "It's a bit dark"  — mean luma of a downscaled sample below the exposure floor the gates already use (LUMA_MIN)
 *   "Hold still"       — frame-to-frame difference of the mean-removed sample above MOTION_DIFF (a global exposure
 *                        change is not movement); clears after ~2 s steady
 *   "Move closer/back" — SELFIE ONLY, from the face box the browser's FaceDetector reports on the sample (when it exists)
 *
 * Not claimed: "the board is cut off" — needs an on-device model (§5, named backlog).
 */

import { useEffect, useRef, useState } from 'react';
import { LUMA_MIN } from './thresholds';

export type FramingHint = 'dark' | 'moving' | 'closer' | 'back';

export const HINT_SAMPLE_MS = 500;
export const HINT_SAMPLE_W = 32;
/** Below this the frame is murk (the same floor the still/clip gates use). */
export const DARK_LUMA = LUMA_MIN;
/** …and it must climb this far above the floor before the hint clears (no flicker at the edge). */
export const DARK_CLEAR_LUMA = LUMA_MIN + 8;
/** Mean absolute luma change between two samples that reads as movement (0–255 scale). */
export const MOTION_DIFF = 10;
/** Consecutive steady samples (× HINT_SAMPLE_MS ≈ 2 s) before "Hold still" clears. */
export const STEADY_SAMPLES = 4;
/** Face width as a fraction of the frame width: closer below, back above. */
export const FACE_MIN_RATIO = 0.28;
export const FACE_MAX_RATIO = 0.7;

export interface FramingSample {
  readonly meanLuma: number;
  /** Mean absolute luma difference to the previous sample, or null for the first sample. */
  readonly diff: number | null;
  /** Face width / frame width from the browser's detector, or null when none ran. */
  readonly faceWidthRatio: number | null;
}

export interface FramingState {
  readonly hint: FramingHint | null;
  readonly steady: number;
}

export const FRAMING_IDLE: FramingState = { hint: null, steady: 0 };

/** The pure step: the next hint from the previous state and one sample. Priority: dark > moving > closer/back. */
export function nextFraming(prev: FramingState, s: FramingSample): FramingState {
  const dark = prev.hint === 'dark' ? s.meanLuma < DARK_CLEAR_LUMA : s.meanLuma < DARK_LUMA;
  if (dark) return { hint: 'dark', steady: 0 };
  const moving = s.diff !== null && s.diff > MOTION_DIFF;
  const steady = moving ? 0 : prev.steady + 1;
  if (moving) return { hint: 'moving', steady: 0 };
  if (prev.hint === 'moving' && steady < STEADY_SAMPLES) return { hint: 'moving', steady };
  if (s.faceWidthRatio !== null) {
    if (s.faceWidthRatio < FACE_MIN_RATIO) return { hint: 'closer', steady };
    if (s.faceWidthRatio > FACE_MAX_RATIO) return { hint: 'back', steady };
  }
  return { hint: null, steady };
}

/** Coarse luma per pixel of an RGBA buffer (Rec. 601 weights). */
export function lumaOf(data: Uint8ClampedArray): Float32Array {
  const out = new Float32Array(data.length / 4);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) out[p] = 0.299 * (data[i] as number) + 0.587 * (data[i + 1] as number) + 0.114 * (data[i + 2] as number);
  return out;
}

export function meanAbsDiff(a: Float32Array, b: Float32Array): number {
  const n = Math.min(a.length, b.length);
  if (n === 0) return 0;
  let s = 0;
  for (let i = 0; i < n; i += 1) s += Math.abs((a[i] as number) - (b[i] as number));
  return s / n;
}

/**
 * The same sample with its mean removed: a global exposure change (a cloud, auto-exposure settling, a light switched
 * on) shifts every pixel alike and must NOT read as "Hold still"; movement changes the PATTERN, which survives centring.
 */
export function centred(luma: Float32Array): Float32Array {
  const n = luma.length;
  if (n === 0) return luma;
  let mean = 0;
  for (const v of luma) mean += v;
  mean /= n;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i += 1) out[i] = (luma[i] as number) - mean;
  return out;
}

type FaceDetectorLike = new (opts?: { fastMode?: boolean; maxDetectedFaces?: number }) => { detect: (src: CanvasImageSource) => Promise<{ boundingBox: { width: number } }[]> };

/**
 * Sample the live `<video>` every HINT_SAMPLE_MS while enabled; selfie shots also ask the browser's FaceDetector (where it
 * exists) for a face box. Returns the current hint or null.
 */
export function useFramingHints(getVideo: () => HTMLVideoElement | null, enabled: boolean, selfie: boolean): FramingHint | null {
  const [hint, setHint] = useState<FramingHint | null>(null);
  const state = useRef<FramingState>(FRAMING_IDLE);
  const prevLuma = useRef<Float32Array | null>(null);

  useEffect(() => {
    state.current = FRAMING_IDLE;
    prevLuma.current = null;
    setHint(null);
    if (!enabled || typeof document === 'undefined') return undefined;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return undefined; // cannot sample — no hint, never a fake one
    const Detector = selfie && typeof window !== 'undefined' ? (window as unknown as { FaceDetector?: FaceDetectorLike }).FaceDetector : undefined;
    const detector = Detector ? new Detector({ fastMode: true, maxDetectedFaces: 1 }) : null;
    let busy = false;
    const timer = window.setInterval(() => {
      const el = getVideo();
      if (busy || !el || el.readyState < 2 || !el.videoWidth || !el.videoHeight) return;
      busy = true;
      void (async () => {
        try {
          const w = HINT_SAMPLE_W;
          const h = Math.max(2, Math.round((el.videoHeight * w) / el.videoWidth));
          canvas.width = w;
          canvas.height = h;
          ctx.drawImage(el, 0, 0, w, h);
          const luma = lumaOf(ctx.getImageData(0, 0, w, h).data);
          let meanLuma = 0;
          for (const v of luma) meanLuma += v;
          meanLuma /= luma.length || 1;
          // motion = the pattern changing, not the exposure changing (see `centred`)
          const diff = prevLuma.current ? meanAbsDiff(centred(luma), centred(prevLuma.current)) : null;
          prevLuma.current = luma;
          let faceWidthRatio: number | null = null;
          if (detector) {
            try {
              const faces = await detector.detect(canvas);
              faceWidthRatio = faces.length > 0 ? (faces[0] as { boundingBox: { width: number } }).boundingBox.width / w : null;
            } catch {
              faceWidthRatio = null;
            }
          }
          state.current = nextFraming(state.current, { meanLuma, diff, faceWidthRatio });
          setHint(state.current.hint);
        } catch {
          setHint(null);
        } finally {
          busy = false;
        }
      })();
    }, HINT_SAMPLE_MS);
    return () => window.clearInterval(timer);
  }, [enabled, selfie, getVideo]);

  return hint;
}
